<div class="shipping-boxes-container">
  @if (viewMode() === 'list') {
    <div class="header-actions">
      <h2>Cajas de envío</h2>
      <button type="button" class="btn-primary" (click)="openNewForm()">+ Nueva caja</button>
    </div>

    <p class="shipping-boxes-help">
      La tienda elige sola la caja activa más chica donde cabe el carrito, usando el peso y las medidas de cada producto,
      y cotiza el despacho con Chilexpress.
      @if (fillFactorPercentage() !== null) {
        Cuando una caja lleva varias piezas se considera solo un {{ fillFactorPercentage() }}% de su volumen por el aire entre ellas.
      }
      Si el pedido no cabe en una caja, se parte en varias y se suma el costo.
    </p>

    <p class="shipping-boxes-help">
      Las cajas solo sirven para cotizar: al despachar puedes usar otra. Los pedidos ya hechos conservan la caja con que se cotizaron.
      Las cajas no se borran, se desactivan, y siempre debe quedar al menos una activa.
    </p>

    @if (errorMessage()) {
      <p class="notice notice--error" role="alert">{{ errorMessage() }}</p>
    }

    @if (productsWithoutBox().length > 0) {
      <div class="notice notice--warning" role="alert">
        <p>Con las cajas activas actuales, estos productos ya no caben en ninguna y quedan solo para retiro en tienda:</p>
        <ul>
          @for (product of productsWithoutBox(); track product.productName + product.sizeLabel) {
            <li>{{ product.productName }}{{ product.sizeLabel ? ' (' + product.sizeLabel + ')' : '' }}</li>
          }
        </ul>
      </div>
    }

    <div class="table-container">
      <table class="admin-table">
        <thead>
          <tr>
            <th>Caja</th>
            <th>Medidas interiores (cm)</th>
            <th>Peso máximo</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          @if (isLoading()) {
            <tr><td colspan="5" class="table-state">Cargando cajas...</td></tr>
          } @else if (hasLoadError()) {
            <tr><td colspan="5" class="table-state">No pudimos cargar las cajas. Recarga la página para reintentar.</td></tr>
          } @else {
            @for (shippingBox of shippingBoxes(); track shippingBox.id) {
              <tr>
                <td class="fw-bold">{{ shippingBox.label }} ({{ shippingBox.code }})</td>
                <td>{{ shippingBox.lengthCm }} × {{ shippingBox.widthCm }} × {{ shippingBox.heightCm }}</td>
                <td>{{ shippingBox.maxWeightGrams / 1000 }} kg</td>
                <td>
                  <span class="status-badge" [class.active]="shippingBox.isActive">
                    {{ shippingBox.isActive ? 'Activa' : 'Inactiva' }}
                  </span>
                </td>
                <td>
                  <div class="action-buttons">
                    <button type="button" class="btn-icon edit" (click)="openEditForm(shippingBox)">Editar</button>
                    <button type="button" class="btn-icon toggle" [disabled]="togglingBoxId() !== null" (click)="toggleActive(shippingBox)">
                      {{ togglingBoxId() === shippingBox.id ? 'Guardando...' : shippingBox.isActive ? 'Desactivar' : 'Activar' }}
                    </button>
                  </div>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  } @else {
    <div class="header-actions">
      <h2>{{ editingBoxId ? 'Editar caja' : 'Nueva caja' }}</h2>
      <button type="button" class="btn-outline" (click)="closeForm()">Volver a la lista</button>
    </div>

    <form [formGroup]="boxForm" (ngSubmit)="onSubmit()" class="box-form">
      @if (errorMessage()) {
        <p class="notice notice--error" role="alert">{{ errorMessage() }}</p>
      }

      <div class="form-row">
        <div class="form-group">
          <label for="box-code">Código *</label>
          <input id="box-code" type="text" formControlName="code" placeholder="Ej: XL" maxlength="10">
          @if (hasFieldError('code')) {
            <span class="error-msg">Obligatorio, hasta 10 caracteres (letras, números o guion).</span>
          }
        </div>

        <div class="form-group">
          <label for="box-label">Nombre *</label>
          <input id="box-label" type="text" formControlName="label" placeholder="Ej: Extra grande" maxlength="50">
          @if (hasFieldError('label')) {
            <span class="error-msg">El nombre es obligatorio (máximo 50 caracteres).</span>
          }
        </div>
      </div>

      <div class="form-row form-row--three">
        <div class="form-group">
          <label for="box-length">Largo (cm) *</label>
          <input id="box-length" type="number" formControlName="lengthCm" min="1" max="200" step="1">
          @if (hasFieldError('lengthCm')) {
            <span class="error-msg">Entre 1 y 200.</span>
          }
        </div>

        <div class="form-group">
          <label for="box-width">Ancho (cm) *</label>
          <input id="box-width" type="number" formControlName="widthCm" min="1" max="200" step="1">
          @if (hasFieldError('widthCm')) {
            <span class="error-msg">Entre 1 y 200.</span>
          }
        </div>

        <div class="form-group">
          <label for="box-height">Alto (cm) *</label>
          <input id="box-height" type="number" formControlName="heightCm" min="1" max="200" step="1">
          @if (hasFieldError('heightCm')) {
            <span class="error-msg">Entre 1 y 200.</span>
          }
        </div>
      </div>

      <div class="form-group">
        <label for="box-weight">Peso máximo (gramos) *</label>
        <input id="box-weight" type="number" formControlName="maxWeightGrams" min="1" step="1">
        @if (hasFieldError('maxWeightGrams')) {
          <span class="error-msg">Debe ser un entero de al menos 1 g.</span>
        }
      </div>

      <p class="form-hint">Usa las medidas interiores. Si cambias una caja, solo afecta las cotizaciones futuras.</p>

      <div class="form-actions">
        <button type="button" class="btn-outline" (click)="closeForm()">Cancelar</button>
        <button type="submit" class="btn-primary" [disabled]="isSubmitting()">
          {{ isSubmitting() ? 'Guardando...' : 'Guardar caja' }}
        </button>
      </div>
    </form>
  }
</div>
